import { onMounted, onUnmounted, ref, type Ref } from "vue";

interface UsePollingOptions<T> {
  fn: () => Promise<T>;
  /** Interval in ms when document is visible. */
  intervalVisible?: number;
  /** Interval in ms when document is hidden. */
  intervalHidden?: number;
  /** Back-off interval after `failureThreshold` consecutive failures. */
  intervalBackoff?: number;
  failureThreshold?: number;
  onResult?: (v: T) => void;
  onError?: (e: unknown) => void;
}

interface UsePollingHandle<T> {
  data: Ref<T | null>;
  error: Ref<unknown>;
  lostConnection: Ref<boolean>;
  /** Number of consecutive failures. */
  failureCount: Ref<number>;
  lastSuccessAt: Ref<number | null>;
  stop: () => void;
  refresh: () => Promise<void>;
}

/**
 * Reusable status poller with visibility-aware interval and failure back-off.
 * Spec: ui-ux-design.md §3.4 — "2s interval while visible, 10s when hidden,
 * back off to 30s after 3 failed polls. Auto-clears banner on next success."
 */
export function usePolling<T>(opts: UsePollingOptions<T>): UsePollingHandle<T> {
  const data = ref<T | null>(null) as Ref<T | null>;
  const error = ref<unknown>(null);
  const failureCount = ref(0);
  const lastSuccessAt = ref<number | null>(null);
  const lostConnection = ref(false);

  const intervalVisible = opts.intervalVisible ?? 2000;
  const intervalHidden = opts.intervalHidden ?? 10_000;
  const intervalBackoff = opts.intervalBackoff ?? 30_000;
  const failureThreshold = opts.failureThreshold ?? 3;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  function currentInterval() {
    if (failureCount.value >= failureThreshold) return intervalBackoff;
    return document.hidden ? intervalHidden : intervalVisible;
  }

  async function tick() {
    if (stopped) return;
    try {
      const v = await opts.fn();
      data.value = v;
      error.value = null;
      failureCount.value = 0;
      lastSuccessAt.value = Date.now();
      lostConnection.value = false;
      opts.onResult?.(v);
    } catch (e) {
      error.value = e;
      failureCount.value += 1;
      if (failureCount.value >= failureThreshold) {
        lostConnection.value = true;
      }
      opts.onError?.(e);
    } finally {
      if (!stopped) {
        timer = setTimeout(tick, currentInterval());
      }
    }
  }

  function onVisibility() {
    // No need to reschedule mid-flight; the next tick picks up the new interval.
    if (!document.hidden && timer) {
      clearTimeout(timer);
      timer = setTimeout(tick, 0);
    }
  }

  async function refresh() {
    if (timer) clearTimeout(timer);
    await tick();
  }

  function stop() {
    stopped = true;
    if (timer) clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisibility);
  }

  onMounted(() => {
    document.addEventListener("visibilitychange", onVisibility);
    void tick();
  });

  onUnmounted(stop);

  return { data, error, lostConnection, failureCount, lastSuccessAt, stop, refresh };
}
